"use client";

import type React from "react";

import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface AnimatedShapeProps {
  children: React.ReactNode;
  initialX: number;
  initialY: number;
  delay?: number;
}

const AnimatedShape = ({
  children,
  initialX,
  initialY,
  delay = 0,
}: AnimatedShapeProps) => {
  const [windowSize, setWindowSize] = useState({ width: 1280, height: 1026 });

  useEffect(() => {
    const updateWindowSize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);
    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  // Generate random movement within bounds
  const generateRandomPath = () => {
    const paths = [];
    for (let i = 0; i < 4; i++) {
      paths.push({
        x: Math.random() * (windowSize.width - 200) + 100,
        y: Math.random() * (windowSize.height - 200) + 100,
      });
    }
    return paths;
  };

  return (
    <motion.g
      initial={{ x: initialX, y: initialY, opacity: 0 }}
      animate={{
        x: generateRandomPath().map((p) => p.x),
        y: generateRandomPath().map((p) => p.y),
        opacity: [0, 0.8, 0.6, 0.9, 0.7],
        scale: [1, 1.1, 0.9, 1.05, 1],
      }}
      transition={{
        duration: 20 + Math.random() * 10,
        repeat: Number.POSITIVE_INFINITY,
        repeatType: "reverse",
        ease: "easeInOut",
        delay,
        times: [0, 0.25, 0.5, 0.75, 1],
      }}
    >
      {children}
    </motion.g>
  );
};

export default function AnimatedBackground() {
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden">
      <div
        className="absolute inset-0 w-full h-full"
        style={{
          background: "linear-gradient(135deg, #f5f5dc 0%, #e6f3e6 100%)",
        }}
      />

      <svg
        width="100%"
        height="100%"
        viewBox="0 0 1280 1026"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute inset-0 w-full h-full"
      >
        <defs>
          <filter
            id="filter0_f"
            x="-527"
            y="102.09"
            width="681.311"
            height="650.729"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="100"
              result="effect1_foregroundBlur"
            />
          </filter>
          <filter
            id="filter1_f"
            x="-340.198"
            y="-301"
            width="1081.31"
            height="1050.73"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="200"
              result="effect1_foregroundBlur"
            />
          </filter>
          <filter
            id="filter2_f"
            x="150.689"
            y="-223.273"
            width="1081.31"
            height="1050.73"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="200"
              result="effect1_foregroundBlur"
            />
          </filter>
          <filter
            id="filter3_f"
            x="19"
            y="547.93"
            width="676.456"
            height="676.456"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="100"
              result="effect1_foregroundBlur"
            />
          </filter>
          <filter
            id="filter4_f"
            x="199.127"
            y="124"
            width="1076.46"
            height="1076.46"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="200"
              result="effect1_foregroundBlur"
            />
          </filter>
          <filter
            id="filter5_f"
            x="681.544"
            y="209.701"
            width="1076.46"
            height="1076.46"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="200"
              result="effect1_foregroundBlur"
            />
          </filter>
        </defs>

        {/* Ellipse 1 - Cream */}
        <AnimatedShape initialX={-186.345} initialY={427.454} delay={0}>
          <ellipse
            cx="0"
            cy="0"
            rx="140.655"
            ry="125.364"
            fill="#FFF3E0"
            filter="url(#filter0_f)"
          />
        </AnimatedShape>

        {/* Ellipse 2 - Yellow */}
        <AnimatedShape initialX={200.457} initialY={224.364} delay={2}>
          <ellipse
            cx="0"
            cy="0"
            rx="140.655"
            ry="125.364"
            fill="#FFEB3B"
            filter="url(#filter1_f)"
          />
        </AnimatedShape>

        {/* Ellipse 3 - Green */}
        <AnimatedShape initialX={691.345} initialY={302.091} delay={4}>
          <ellipse
            cx="0"
            cy="0"
            rx="140.655"
            ry="125.364"
            fill="#2E7D32"
            filter="url(#filter2_f)"
          />
        </AnimatedShape>

        {/* Circle 1 - Cream */}
        <AnimatedShape initialX={357.228} initialY={886.158} delay={1}>
          <circle
            cx="0"
            cy="0"
            r="138.228"
            fill="#FFF3E0"
            filter="url(#filter3_f)"
          />
        </AnimatedShape>

        {/* Circle 2 - Yellow */}
        <AnimatedShape initialX={737.355} initialY={662.228} delay={3}>
          <circle
            cx="0"
            cy="0"
            r="138.228"
            fill="#FFEB3B"
            filter="url(#filter4_f)"
          />
        </AnimatedShape>

        {/* Circle 3 - Green */}
        <AnimatedShape initialX={1219.77} initialY={747.929} delay={5}>
          <circle
            cx="0"
            cy="0"
            r="138.228"
            fill="#2E7D32"
            filter="url(#filter5_f)"
          />
        </AnimatedShape>
      </svg>
    </div>
  );
}
